import React, { useState, useEffect, useMemo } from "react";

const COMPANIES_PER_PAGE = 6;

/** Si Dashboard / EnergyMeterDetailChint — vetëm kompanitë HIB. */
const isHibDashboardCompanyName = (name) =>
    String(name || "").toUpperCase().includes("HIB");

/** PV Now në gauge: HIB → Ppv (fusha `ppv` / `ppvNow`), të tjerët → `pvNow`. */
const getCompanyPvNowMw = (company) => {
    if (isHibDashboardCompanyName(company?.name)) {
        const ppv = company.ppv ?? company.ppvNow;
        if (ppv != null && Number.isFinite(Number(ppv))) {
            return Math.abs(Number(ppv));
        }
    }
    const v = company.pvNow;
    return Number.isFinite(Number(v)) ? Math.abs(Number(v)) : 0;
};

/**
 * ProductionByCompanies — COMPLETELY new visual language.
 *
 * Instead of a grid of cards, each company is rendered as a large
 * circular gauge (SVG donut) whose arc represents production / capacity.
 * Only the raw data is preserved: company name, current PV output (MW),
 * installed capacity (MW), and online/offline status.
 *
 * Props:
 *   companies: Array<{ id?, name, pvNow, ppv?, capacity, online }> — HIB: prefero `ppv` (MW) në vend të pv_now
 *   onViewAll?: () => void
 *   onCompanyClick?: (company) => void — p.sh. hap /devices?company=…
 *   viewAllLabel?: string — teksti i butonit (p.sh. nga i18n)
 */
const ProductionByCompanies = ({ companies = [], onViewAll, onCompanyClick, viewAllLabel = "All companies →" }) => {
    const [page, setPage] = useState(0);

    const totalPages = Math.max(1, Math.ceil(companies.length / COMPANIES_PER_PAGE));

    useEffect(() => {
        setPage((p) => {
            const tp = Math.max(1, Math.ceil(companies.length / COMPANIES_PER_PAGE));
            return Math.min(Math.max(0, p), tp - 1);
        });
    }, [companies.length]);

    const visibleCompanies = useMemo(
        () => companies.slice(page * COMPANIES_PER_PAGE, page * COMPANIES_PER_PAGE + COMPANIES_PER_PAGE),
        [companies, page],
    );

    const totalPv = companies.reduce((s, c) => s + getCompanyPvNowMw(c), 0);
    const totalCap = companies.reduce((s, c) => s + c.capacity, 0);
    const onlineCount = companies.filter((c) => c.online).length;

    return (
        <section
            data-testid="production-by-companies-panel"
            className="relative flex h-full min-h-0 w-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white p-3 shadow-sm dark:border-white/5 dark:bg-[#0a0f22] dark:shadow-none sm:p-3.5"
        >
            <div className="pointer-events-none absolute -top-10 left-1/2 h-24 w-[56%] -translate-x-1/2 rounded-full bg-emerald-500/5 blur-2xl dark:bg-emerald-500/10" />

            {/* Header */}
            <header className="relative mb-3 flex shrink-0 flex-col gap-2 border-b border-dashed border-gray-200 pb-2.5 dark:border-white/10 sm:flex-row sm:items-end sm:justify-between sm:gap-3">
                <div className="min-w-0">
                    <p className="text-[8px] font-semibold uppercase tracking-[0.18em] text-emerald-600 dark:text-emerald-400/80 sm:text-[9px]">
                        Fleet Output
                    </p>
                    <h2
                        data-testid="panel-title"
                        className="mt-0.5 truncate text-sm font-semibold tracking-tight text-gray-900 dark:text-white sm:text-base"
                    >
                        Production by companies
                    </h2>
                </div>

                <div className="flex flex-wrap items-end justify-end gap-x-2 gap-y-1.5 sm:gap-x-3">
                    <InlineStat
                        label="Live output"
                        value={`${totalPv.toFixed(2)} MW`}
                        accent
                    />
                    <InlineStat
                        label="Total capacity"
                        value={`${totalCap.toFixed(2)} MW`}
                    />
                    <InlineStat
                        label="Online"
                        value={`${onlineCount}/${companies.length || 0}`}
                    />
                    <button
                        data-testid="view-all-companies-btn"
                        type="button"
                        onClick={() => onViewAll?.()}
                        className="rounded-md border border-violet-300 px-2.5 py-1.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-violet-700 transition hover:bg-violet-50 hover:text-violet-900 dark:border-violet-400/30 dark:text-violet-300 dark:hover:bg-violet-500/10 dark:hover:text-violet-200 sm:text-[10px]"
                    >
                        {viewAllLabel}
                    </button>
                </div>
            </header>

            {/* Gauge grid — 3 për rresht, max 6 për faqe */}
            <div className="relative grid min-h-0 min-w-0 flex-1 grid-cols-3 content-start gap-x-2 gap-y-6 sm:gap-x-2.5 sm:gap-y-8">
                {visibleCompanies.map((c, idx) => {
                    const globalIdx = page * COMPANIES_PER_PAGE + idx;
                    return (
                        <GaugeTile
                            key={c.id != null ? String(c.id) : `${c.name}-${globalIdx}`}
                            company={c}
                            index={globalIdx}
                            onCompanyClick={onCompanyClick}
                        />
                    );
                })}
            </div>

            {companies.length > COMPANIES_PER_PAGE && (
                <nav
                    className="mt-3 flex shrink-0 justify-center gap-2 pb-0.5"
                    aria-label="Faqet e kompanive"
                >
                    {Array.from({ length: totalPages }).map((_, idx) => (
                        <button
                            key={idx}
                            type="button"
                            onClick={() => setPage(idx)}
                            aria-current={page === idx ? "true" : undefined}
                            aria-label={`Faqja ${idx + 1}`}
                            className={`h-2 rounded-full transition-all duration-300 ${page === idx
                                ? "w-6 bg-cyan-600 shadow-sm dark:bg-cyan-500 dark:shadow-[0_0_10px_rgba(6,182,212,0.45)]"
                                : "w-2 bg-gray-300 hover:bg-gray-400 dark:bg-slate-500/70 dark:hover:bg-slate-400"
                                }`}
                        />
                    ))}
                </nav>
            )}
        </section>
    );
};

const InlineStat = ({ label, value, accent = false }) => (
    <div className="flex flex-col items-end">
        <span className="text-[8px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-slate-500 sm:text-[9px]">
            {label}
        </span>
        <span
            className={`font-mono text-xs font-bold leading-tight sm:text-sm ${accent
                ? "text-emerald-600 dark:text-emerald-400 dark:[text-shadow:0_0_10px_rgba(52,211,153,0.5)]"
                : "text-gray-900 dark:text-white"
                }`}
        >
            {value}
        </span>
    </div>
);

/**
 * Circular SVG gauge (3/4 arc, 270°) — shows production as % of capacity,
 * with the MW number in the centre and company name + status below.
 */
const GaugeTile = ({ company, index, onCompanyClick }) => {
    const { name, capacity, online } = company;
    const pvNow = getCompanyPvNowMw(company);
    const pct = capacity > 0 ? Math.min(100, (pvNow / capacity) * 100) : 0;

    // Geometry — 3 kolona, pak më i madh që të mbushë hapësirën
    const size = 82;
    const stroke = 5;
    const r = (size - stroke) / 2 - 2.5;
    const cx = size / 2;
    const cy = size / 2;
    const circ = 2 * Math.PI * r;
    const sweep = circ * 0.75; // 75% of full circle = 270°
    const dash = (pct / 100) * sweep;

    const color = online ? "#34d399" : "#f43f5e";
    const softColor = online ? "rgba(52,211,153,0.25)" : "rgba(244,63,94,0.2)";
    const uid = `grad-${index}`;

    // start the arc at the bottom-left: rotate -135° around centre
    const inner = (
        <>
            <div className="relative">
                <svg
                    width={size}
                    height={size}
                    className="transition-transform duration-300 group-hover:scale-[1.03]"
                    style={{ filter: online ? `drop-shadow(0 0 8px ${softColor})` : "none" }}
                >
                    <defs>
                        <linearGradient id={uid} x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor={color} stopOpacity="0.6" />
                            <stop offset="100%" stopColor={color} stopOpacity="1" />
                        </linearGradient>
                    </defs>

                    {/* Background arc */}
                    <circle
                        cx={cx}
                        cy={cy}
                        r={r}
                        fill="none"
                        className="stroke-slate-200 dark:stroke-white/[0.06]"
                        strokeWidth={stroke}
                        strokeDasharray={`${sweep} ${circ}`}
                        strokeLinecap="round"
                        transform={`rotate(135 ${cx} ${cy})`}
                    />
                    {/* Value arc */}
                    <circle
                        cx={cx}
                        cy={cy}
                        r={r}
                        fill="none"
                        stroke={`url(#${uid})`}
                        strokeWidth={stroke}
                        strokeDasharray={`${dash} ${circ}`}
                        strokeLinecap="round"
                        transform={`rotate(135 ${cx} ${cy})`}
                        style={{ transition: "stroke-dasharray 800ms ease-out" }}
                    />

                    {/* Tick marks every 10% on the arc */}
                    {Array.from({ length: 10 }).map((_, i) => {
                        const t = i / 9;
                        // arc spans 270°, starts at 135° (bottom-left), ends at 45° (bottom-right going clockwise through top)
                        const angleDeg = 135 + t * 270;
                        const angleRad = (angleDeg * Math.PI) / 180;
                        const rInner = r - stroke / 2 - 2;
                        const rOuter = r - stroke / 2 - 0.5;
                        const x1 = cx + rInner * Math.cos(angleRad);
                        const y1 = cy + rInner * Math.sin(angleRad);
                        const x2 = cx + rOuter * Math.cos(angleRad);
                        const y2 = cy + rOuter * Math.sin(angleRad);
                        return (
                            <line
                                key={i}
                                x1={x1}
                                y1={y1}
                                x2={x2}
                                y2={y2}
                                className="stroke-slate-400/70 dark:stroke-white/25"
                                strokeWidth={1}
                            />
                        );
                    })}

                    {/* Centre content */}
                    <text
                        x={cx}
                        y={cy - 3}
                        textAnchor="middle"
                        className="fill-gray-900 font-mono dark:fill-white"
                        style={{ fontSize: 13, fontWeight: 700 }}
                    >
                        {pvNow.toFixed(2)}
                    </text>
                    <text
                        x={cx}
                        y={cy + 8}
                        textAnchor="middle"
                        className="fill-gray-500 dark:fill-slate-400"
                        style={{
                            fontSize: 6,
                            fontWeight: 600,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                        }}
                    >
                        MW · NOW
                    </text>

                    {/* Bottom label (under the arc gap) */}
                    <text
                        x={cx}
                        y={cy + 21}
                        textAnchor="middle"
                        className="fill-gray-600 dark:fill-slate-500"
                        style={{
                            fontSize: 6,
                            fontWeight: 600,
                            letterSpacing: "0.05em",
                            textTransform: "uppercase",
                        }}
                    >
                        {pct.toFixed(0)}% of {capacity.toFixed(2)} MW
                    </text>
                </svg>

                {/* Status pip floating over the gauge */}
                <span
                    className={`absolute right-1.5 top-1.5 flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[7px] font-bold uppercase tracking-[0.1em] sm:right-2 sm:top-2 sm:text-[8px] ${online
                        ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-400/30"
                        : "bg-rose-50 text-rose-700 ring-1 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-400/30"
                        }`}
                >
                    <span
                        className={`h-1 w-1 rounded-full ${online ? "bg-emerald-500 dark:bg-emerald-400" : "bg-rose-500 dark:bg-rose-400"
                            }`}
                    />
                    {online ? "ON" : "OFF"}
                </span>
            </div>

            {/* Company name below */}
            <h3 className="mt-1 w-full max-w-full truncate px-0.5 text-center text-[8px] font-bold uppercase leading-tight tracking-[0.08em] text-gray-900 dark:text-white sm:text-[9px]">
                {name}
            </h3>
        </>
    );

    return (
        <div
            data-testid={`company-gauge-${name.toLowerCase().replace(/\s+/g, "-")}`}
            className="group flex min-w-0 flex-col items-center"
            style={{ animationDelay: `${index * 80}ms` }}
        >
            {typeof onCompanyClick === "function" ? (
                <button
                    type="button"
                    className="flex min-w-0 flex-col items-center rounded-lg outline-none transition hover:opacity-95 focus-visible:ring-2 focus-visible:ring-violet-400/60 dark:focus-visible:ring-violet-400/50"
                    onClick={() => onCompanyClick(company)}
                >
                    {inner}
                </button>
            ) : (
                inner
            )}
        </div>
    );
};

export default ProductionByCompanies;
